// Flussdiagramm-Editor: SVG-Arbeitsfläche, Palette, Box-Inhalte, Schritt-für-Schritt-Ablauf, Prüfen.
// Bedienung ausschließlich über Pointer Events, damit Finger, Stift und Maus gleich funktionieren.

import { confetti } from "./confetti.ts";
import { ICON } from "./icons.ts";
import { BOX_LABEL, ExprError, Runner, check, compileBox, compileFlow, exits, starsFor } from "./sim.ts";
import type { CheckResult, Flow, FlowNode, Program, Task } from "./sim.ts";
import { store } from "./store.ts";

const W = 1000;
const MIN_H = 720;
const SNAP = 10;
const GAP = 64;        // senkrechter Abstand zwischen automatisch platzierten Boxen
const TAP_SLOP = 6;    // Pixel, ab denen eine Berührung als Ziehen gilt
const STUB = 22;       // gerades Stück an Aus- und Eingängen
const PLAY_MS = 650;

const SIZE: Record<string, { w: number; h: number }> = {
  start: { w: 150, h: 60 }, ende: { w: 150, h: 60 },
  verarbeitung: { w: 230, h: 72 }, eingabe: { w: 230, h: 72 }, ausgabe: { w: 230, h: 72 },
  entscheidung: { w: 250, h: 116 },
};
const PALETTE = ["eingabe", "verarbeitung", "entscheidung", "ausgabe", "ende"];
const PLACEHOLDER: Record<string, string> = {
  verarbeitung: "x = …", eingabe: "a, b", ausgabe: "x", entscheidung: "a > b ?",
};
const EXAMPLE: Record<string, string> = {
  verarbeitung: "Zuweisung, z. B. summe = summe + x",
  eingabe: "Variablen, die eingelesen werden, z. B. a, b",
  ausgabe: 'Werte oder Texte, z. B. "Ergebnis:", x',
  entscheidung: "Bedingung, z. B. a > b oder b ≠ 0",
};
const SYMBOL_KEYS = ["=", "≠", "<", "≤", ">", "≥", "+", "−", "*", "/", "mod", "und", "oder", "(", ")", ","];

type Point = { x: number; y: number };
type Selection = { kind: "node" | "edge"; id: string } | null;
type Drag =
  | { kind: "move"; id: string; start: Point; offset: Point; moved: boolean; client: Point }
  | { kind: "edge"; exit: string; at: Point; client: Point; moved: boolean }
  | { kind: "new"; type: string; at: Point | null };

const esc = (s: string) => s.replace(/[&<>"]/g, c => `&#${c.charCodeAt(0)};`);
const snap = (v: number) => Math.round(v / SNAP) * SNAP;
const size = (n: FlowNode) => SIZE[n.type] ?? SIZE.verarbeitung;
const isBox = (t: string) => t !== "start" && t !== "ende";

/** Umriss eines Knotentyps, zentriert um (0, 0). */
function shape(type: string, cls = "shape"): string {
  const { w, h } = SIZE[type];
  const x = w / 2, y = h / 2;
  switch (type) {
    case "start": case "ende":
      return `<rect x="${-x}" y="${-y}" width="${w}" height="${h}" rx="${y}" class="${cls}"/>`;
    case "eingabe": case "ausgabe":
      return `<polygon points="${-x + 20},${-y} ${x},${-y} ${x - 20},${y} ${-x},${y}" class="${cls}"/>`;
    case "entscheidung":
      return `<polygon points="0,${-y} ${x},0 0,${y} ${-x},0" class="${cls}"/>`;
  }
  return `<rect x="${-x}" y="${-y}" width="${w}" height="${h}" rx="4" class="${cls}"/>`;
}

export function boxSymbolSvg(type: string): string {
  const { w, h } = SIZE[type];
  return `<svg viewBox="${-w / 2 - 6} ${-h / 2 - 6} ${w + 12} ${h + 12}" aria-hidden="true">${shape(type)}</svg>`;
}

/** Andockpunkt als Dreieck in Flussrichtung (unten bzw. rechts); Spitze bei `tip`. */
function tri(tip: Point, right: boolean, s: number): string {
  const pts = right
    ? [[tip.x - s, tip.y - s], [tip.x - s, tip.y + s], [tip.x, tip.y]]
    : [[tip.x - s, tip.y - s], [tip.x + s, tip.y - s], [tip.x, tip.y]];
  return pts.map(p => p.join(",")).join(" ");
}

/** Schriftgröße und ggf. gekürzter Text, damit der Inhalt in die Box passt. */
function fit(text: string, maxW: number): { s: string; size: number } {
  const per = 0.56; // mittlere Zeichenbreite in em
  const sizeFor = (len: number) => Math.min(20, maxW / (Math.max(len, 1) * per));
  if (sizeFor(text.length) >= 13) return { s: text, size: sizeFor(text.length) };
  const keep = Math.max(1, Math.floor(maxW / (13 * per)) - 1);
  return { s: text.slice(0, keep) + "…", size: 13 };
}

export class Editor {
  private flow: Flow;
  private selection: Selection = null;
  private drag: Drag | null = null;
  private undoStack: string[] = [];
  private editing = false;
  private result: CheckResult | null = null;
  private hintsShown: number;
  private caseIdx = 0;
  private runner: Runner | null = null;
  private prevEnv = new Map<string, number>();
  private playTimer: number | null = null;
  private popStars = false;
  private H = MIN_H;

  private readonly root: HTMLElement;
  private readonly task: Task;
  private readonly next: { id: string; titel: string } | null;
  private readonly svg: SVGSVGElement;
  private readonly layer: SVGGElement;
  private readonly palette: HTMLElement;
  private readonly side: HTMLElement;
  private readonly editBar: HTMLElement;

  constructor(root: HTMLElement, task: Task, next: { id: string; titel: string } | null = null) {
    this.root = root;
    this.task = task;
    this.next = next;
    this.flow = store.flow(task.id) ?? Editor.emptyFlow();
    this.hintsShown = store.hints(task.id);

    root.innerHTML = `
      <section class="task-head">
        <a class="back" href="#">← Aufgaben</a>
        <h1>${esc(task.titel)}${task.optional ? ` <span class="tag optional">Optional</span>` : ""}</h1>
        <p class="barometer" hidden></p>
        ${task.text ? `<p>${esc(task.text)}</p>` : ""}
      </section>
      <div class="workbench">
        <aside class="palette" aria-label="Bausteine"></aside>
        <div class="canvas-wrap">
          <svg class="canvas" viewBox="0 0 ${W} ${this.H}" role="application" aria-label="Arbeitsfläche">
            <defs>
              ${["", "hot", "sel"].map(k => `<marker id="arrow${k}" viewBox="0 0 10 10" refX="10" refY="5"
                markerWidth="14" markerHeight="14" markerUnits="userSpaceOnUse" orient="auto">
                <path d="M0 0L10 5L0 10z" class="arrowhead ${k}"/></marker>`).join("")}
            </defs>
            <g class="layer"></g>
          </svg>
          <div class="edit-bar" hidden></div>
          <div class="toolbar">
            <button data-act="undo" title="Rückgängig">${ICON.undo}<span>Rückgängig</span></button>
            <button data-act="delete" title="Auswahl löschen">${ICON.trash}<span>Löschen</span></button>
            <button data-act="clear" title="Alles entfernen">Neu</button>
            <span class="spacer"></span>
            <button data-act="hint" class="secondary"></button>
            <button data-act="check" class="primary">Prüfen</button>
          </div>
        </div>
        <aside class="side"></aside>
      </div>`;
    this.svg = root.querySelector("svg.canvas")!;
    this.layer = this.svg.querySelector("g.layer")!;
    this.palette = root.querySelector(".palette")!;
    this.side = root.querySelector(".side")!;
    this.editBar = root.querySelector(".edit-bar")!;

    this.svg.addEventListener("pointerdown", e => this.onSvgDown(e));
    this.svg.addEventListener("pointermove", e => this.onMove(e));
    this.svg.addEventListener("pointerup", e => this.onUp(e));
    this.svg.addEventListener("pointercancel", () => { this.drag = null; this.render(); });
    // Scrollen per Touch nur verhindern, wenn pointerdown (kommt vor touchstart) ein Ziehen begonnen hat
    this.svg.addEventListener("touchstart", e => { if (this.drag) e.preventDefault(); }, { passive: false });
    root.querySelector(".toolbar")!.addEventListener("click", e => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act;
      if (act) this.action(act);
    });
    this.side.addEventListener("click", e => {
      const el = e.target as HTMLElement;
      const row = el.closest<HTMLElement>("[data-case]");
      const act = el.closest<HTMLElement>("[data-run]")?.dataset.run;
      if (act) this.runAction(act);
      else if (row) this.selectCase(Number(row.dataset.case));
    });
    document.addEventListener("keydown", this.onKey);
    this.renderPalette();
    this.renderEditBar();
    this.render();
  }

  static emptyFlow(): Flow {
    return {
      nodes: [{ id: "s", type: "start", x: 500, y: 70 }, { id: "e", type: "ende", x: 500, y: 600 }],
      edges: [],
    };
  }

  destroy(): void {
    this.stopPlay();
    document.removeEventListener("keydown", this.onKey);
  }

  // ---------------------------------------------------------------- Geometrie

  private node(id: string): FlowNode | undefined {
    return this.flow.nodes.find(n => n.id === id);
  }

  private exitPos(exit: string): Point {
    const [id, branch] = exit.split(".");
    const n = this.node(id)!;
    const { w, h } = size(n);
    return branch === "nein" ? { x: n.x! + w / 2, y: n.y! } : { x: n.x!, y: n.y! + h / 2 };
  }

  private entryPos(n: FlowNode): Point {
    return { x: n.x!, y: n.y! - size(n).h / 2 };
  }

  private target(exit: string): string | undefined {
    return this.flow.edges.find(e => e.from === exit)?.to;
  }

  private toSvg(e: { clientX: number; clientY: number }): Point {
    const m = this.svg.getScreenCTM();
    if (!m) return { x: 0, y: 0 };
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    return { x: p.x, y: p.y };
  }

  private clamp(type: string, p: Point): Point {
    const { w, h } = SIZE[type];
    return {
      x: snap(Math.min(Math.max(p.x, w / 2 + 10), W - w / 2 - 60)),
      y: snap(Math.max(p.y, h / 2 + 10)),
    };
  }

  private nodeAt(p: Point, except?: string): FlowNode | undefined {
    return this.flow.nodes.find(n => {
      const { w, h } = size(n);
      return n.id !== except && n.type !== "start"
        && Math.abs(p.x - n.x!) <= w / 2 + 16 && Math.abs(p.y - n.y!) <= h / 2 + 16;
    });
  }

  private overlaps(type: string, p: Point, except?: string): boolean {
    const { w, h } = SIZE[type];
    return this.flow.nodes.some(n => n.id !== except
      && Math.abs(n.x! - p.x) < (w + size(n).w) / 2 + 20
      && Math.abs(n.y! - p.y) < (h + size(n).h) / 2 + 24);
  }

  /** Verlauf eines Pfeils: gerade aus dem Ausgang, rechtwinklig, von oben in das Ziel. */
  private route(exit: string, to: FlowNode, lane: number): string {
    const s = this.exitPos(exit);
    const t = this.entryPos(to);
    const right = exit.endsWith(".nein");
    const above = t.y - s.y < 2 * STUB;
    if (!above) {
      if (right && t.x >= s.x + STUB) return `M${s.x} ${s.y}H${t.x}V${t.y}`;
      if (right) return `M${s.x} ${s.y}H${s.x + STUB}V${t.y - STUB}H${t.x}V${t.y}`;
      if (Math.abs(t.x - s.x) < 1) return `M${s.x} ${s.y}V${t.y}`;
      return `M${s.x} ${s.y}V${t.y - STUB}H${t.x}V${t.y}`;
    }
    // Rücksprung (Schleife): rechts an allen Boxen im überstrichenen Bereich vorbei
    const top = t.y - STUB, bottom = s.y + 14;
    const xs = this.flow.nodes.filter(n => n.y! + size(n).h / 2 >= top && n.y! - size(n).h / 2 <= bottom)
      .map(n => n.x! + size(n).w / 2);
    const lx = Math.min(Math.max(...xs, s.x) + 34 + 18 * lane, W - 8);
    return right
      ? `M${s.x} ${s.y}H${lx}V${top}H${t.x}V${t.y}`
      : `M${s.x} ${s.y}V${bottom}H${lx}V${top}H${t.x}V${t.y}`;
  }

  // ---------------------------------------------------------------- Änderungen

  private snapshot(): void {
    this.undoStack.push(JSON.stringify(this.flow));
    if (this.undoStack.length > 100) this.undoStack.shift();
  }

  private changed(): void {
    this.stopPlay();
    this.result = null;
    this.runner = null;
    store.saveFlow(this.task.id, this.flow);
  }

  private mutate(fn: () => void): void {
    this.snapshot();
    fn();
    this.changed();
    this.render();
  }

  private newId(): string {
    let k = 1;
    while (this.node(`n${k}`)) k++;
    return `n${k}`;
  }

  /** Antippen in der Palette: neue Box unter die ausgewählte (bzw. unterste offene) Box hängen. */
  private addConnected(type: string): void {
    const sel = this.selection?.kind === "node" ? this.node(this.selection.id) : undefined;
    const open = (n: FlowNode) => exits(n.type, n.id).filter(x => this.target(x) === undefined);
    const anchor = sel && sel.type !== "ende" ? sel
      : [...this.flow.nodes].filter(n => open(n).length).sort((a, b) => b.y! - a.y!)[0];
    if (!anchor) return this.addAt(type, this.freeSpot(type));

    const free = open(anchor);
    const exit = free[0] ?? exits(anchor.type, anchor.id)[0];
    const insertBefore = free.length ? undefined : this.target(exit);
    const a = size(anchor), b = SIZE[type];
    const p = exit.endsWith(".nein")
      ? this.clamp(type, { x: anchor.x! + a.w / 2 + 40 + b.w / 2, y: anchor.y! + a.h / 2 + GAP + b.h / 2 })
      : this.clamp(type, { x: anchor.x!, y: anchor.y! + a.h / 2 + GAP + b.h / 2 });
    const id = this.newId();
    this.mutate(() => {
      // Platz schaffen: alles unterhalb rutscht nach unten
      if (this.overlaps(type, p, anchor.id)) {
        const dy = b.h + GAP;
        for (const n of this.flow.nodes) if (n.id !== anchor.id && n.y! > anchor.y!) n.y! += dy;
      }
      this.flow.nodes.push({ id, type, text: "", ...p });
      this.flow.edges = this.flow.edges.filter(e => e.from !== exit);
      this.flow.edges.push({ from: exit, to: id });
      if (insertBefore && type !== "ende") this.flow.edges.push({ from: exits(type, id)[0], to: insertBefore });
    });
    this.select({ kind: "node", id });
  }

  private addAt(type: string, at: Point): void {
    const id = this.newId();
    this.mutate(() => this.flow.nodes.push({ id, type, text: "", ...this.clamp(type, at) }));
    this.select({ kind: "node", id });
  }

  private freeSpot(type: string): Point {
    const bottom = Math.max(...this.flow.nodes.map(n => n.y! + size(n).h / 2));
    return { x: W / 2, y: bottom + GAP + SIZE[type].h / 2 };
  }

  private deleteNode(id: string): void {
    if (this.node(id)?.type === "start") return;
    this.mutate(() => {
      this.flow.nodes = this.flow.nodes.filter(n => n.id !== id);
      this.flow.edges = this.flow.edges.filter(e => e.to !== id && e.from.split(".")[0] !== id);
    });
  }

  private connect(exit: string, to: string): void {
    this.mutate(() => {
      // Ein Ausgang hat genau einen Pfeil: ein neuer ersetzt den alten.
      this.flow.edges = this.flow.edges.filter(e => e.from !== exit);
      this.flow.edges.push({ from: exit, to });
    });
  }

  private deleteSelection(): void {
    const sel = this.selection;
    if (!sel) return;
    this.select(null);
    if (sel.kind === "node") this.deleteNode(sel.id);
    else this.mutate(() => { this.flow.edges = this.flow.edges.filter(e => e.from !== sel.id); });
  }

  private select(sel: Selection): void {
    const same = sel?.kind === this.selection?.kind && sel?.id === this.selection?.id;
    this.selection = sel;
    if (!same) this.renderEditBar();
  }

  private action(act: string): void {
    switch (act) {
      case "undo": {
        const prev = this.undoStack.pop();
        if (!prev) return;
        this.flow = JSON.parse(prev);
        this.changed();
        this.select(null);
        this.render();
        return;
      }
      case "delete": return this.deleteSelection();
      case "clear":
        if (this.flow.nodes.length > 2 || this.flow.edges.length) {
          this.select(null);
          this.mutate(() => { this.flow = Editor.emptyFlow(); });
        }
        return;
      case "hint":
        if (this.hintsShown < this.task.hinweise.length) {
          this.hintsShown++;
          store.saveHints(this.task.id, this.hintsShown);
          this.render();
        }
        return;
      case "check": return this.runCheck();
    }
  }

  private runCheck(): void {
    this.stopPlay();
    const r = check(this.task, this.flow);
    this.result = r;
    this.root.dispatchEvent(new CustomEvent("checked", {
      detail: { result: r, flow: this.flow, hints: this.hintsShown },
    }));
    if (r.solved) {
      const stars = starsFor(r, this.hintsShown);
      const before = store.stars();
      store.saveStars(this.task.id, stars);
      this.root.dispatchEvent(new CustomEvent("solved", { detail: { stars, before, result: r } }));
      confetti();
      this.popStars = true;
    } else if (r.falsche_testfaelle.length) {
      this.caseIdx = r.falsche_testfaelle[0];
    }
    this.runner = null;
    this.render();
    this.popStars = false;
  }

  // ---------------------------------------------------------------- Ablauf (Schritt für Schritt)

  private program(): Program | null {
    const prog = compileFlow(this.flow);
    return prog.problems.length ? null : prog;
  }

  private ensureRunner(): Runner | null {
    if (this.runner) return this.runner;
    const prog = this.program();
    if (!prog) return null;
    const tf = this.task.testfaelle[this.caseIdx];
    this.runner = new Runner(prog, tf.eingabe, this.task.max_schritte);
    this.prevEnv = new Map();
    return this.runner;
  }

  private selectCase(i: number): void {
    if (!this.task.testfaelle[i]) return;
    this.stopPlay();
    this.caseIdx = i;
    this.runner = null;
    this.render();
  }

  private stepOnce(): void {
    const r = this.ensureRunner();
    if (!r) return;
    this.prevEnv = new Map(r.env);
    r.step();
  }

  private runAction(act: string): void {
    switch (act) {
      case "step":
        this.stopPlay();
        this.stepOnce();
        break;
      case "play": {
        if (this.playTimer !== null) { this.stopPlay(); break; }
        const r = this.ensureRunner();
        if (!r) return;
        if (r.status !== "läuft") { this.runner = null; this.ensureRunner(); }
        this.playTimer = window.setInterval(() => {
          this.stepOnce();
          if (this.runner?.status !== "läuft") this.stopPlay();
          this.render();
        }, PLAY_MS);
        this.stepOnce();
        break;
      }
      case "end":
        this.stopPlay();
        this.ensureRunner()?.runToEnd();
        this.prevEnv = new Map(this.runner?.env ?? []);
        break;
      case "restart":
        this.stopPlay();
        this.runner = null;
        break;
    }
    this.render();
  }

  private stopPlay(): void {
    if (this.playTimer !== null) window.clearInterval(this.playTimer);
    this.playTimer = null;
  }

  // ---------------------------------------------------------------- Pointer

  private onKey = (e: KeyboardEvent): void => {
    if ((e.target as HTMLElement).closest("input, textarea")) return;
    if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); this.deleteSelection(); }
    if (e.key === "z" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); this.action("undo"); }
    if (e.key === "Escape") { this.select(null); this.render(); }
  };

  private onSvgDown(e: PointerEvent): void {
    if (this.drag || !e.isPrimary) return;
    const el = (e.target as Element).closest<SVGElement>("[data-exit],[data-node],[data-edge]");
    const p = this.toSvg(e);
    const client = { x: e.clientX, y: e.clientY };
    this.svg.setPointerCapture(e.pointerId);

    if (el?.dataset.exit) {
      this.drag = { kind: "edge", exit: el.dataset.exit, at: p, client, moved: false };
    } else if (el?.dataset.node) {
      const n = this.node(el.dataset.node)!;
      this.drag = { kind: "move", id: n.id, start: { x: n.x!, y: n.y! },
                    offset: { x: p.x - n.x!, y: p.y - n.y! }, moved: false, client };
    } else if (el?.dataset.edge) {
      this.select({ kind: "edge", id: el.dataset.edge });
    } else {
      this.select(null);
    }
    this.render();
  }

  private onMove(e: PointerEvent): void {
    const d = this.drag;
    if (!d || d.kind === "new") return;
    if (!d.moved && Math.hypot(e.clientX - d.client.x, e.clientY - d.client.y) < TAP_SLOP) return;
    d.moved = true;
    const p = this.toSvg(e);
    if (d.kind === "move") {
      const n = this.node(d.id)!;
      Object.assign(n, this.clamp(n.type, { x: p.x - d.offset.x, y: p.y - d.offset.y }));
      this.palette.classList.toggle("trash-hot", n.type !== "start" && this.overPalette(e));
    } else {
      d.at = p;
    }
    this.render();
  }

  private onUp(e: PointerEvent): void {
    const d = this.drag;
    if (!d || d.kind === "new") return;
    this.drag = null;
    this.palette.classList.remove("trash-hot");
    if (d.kind === "move") {
      const n = this.node(d.id)!;
      if (!d.moved) {
        const same = this.selection?.kind === "node" && this.selection.id === n.id;
        this.select(same ? null : { kind: "node", id: n.id });
      } else if (n.type !== "start" && this.overPalette(e)) {
        Object.assign(n, d.start);
        this.select(null);
        this.deleteNode(n.id);
        return;
      } else {
        const end = { x: n.x!, y: n.y! };
        Object.assign(n, d.start); // Ausgangszustand für Undo merken
        if (end.x !== d.start.x || end.y !== d.start.y) this.mutate(() => Object.assign(n, end));
      }
    } else if (d.moved) {
      const to = this.nodeAt(this.toSvg(e));
      if (to) this.connect(d.exit, to.id);
    } else if (this.target(d.exit) !== undefined) {
      this.select({ kind: "edge", id: d.exit }); // Antippen eines belegten Ausgangs wählt den Pfeil
    }
    this.render();
  }

  private overPalette(e: { clientX: number; clientY: number }): boolean {
    const r = this.palette.getBoundingClientRect();
    return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
  }

  private onPaletteDown(e: PointerEvent, type: string, btn: HTMLElement): void {
    if (!e.isPrimary) return;
    e.preventDefault();
    btn.setPointerCapture(e.pointerId);
    const start = { x: e.clientX, y: e.clientY };
    const drag: Drag = { kind: "new", type, at: null };
    this.drag = drag;
    const move = (ev: PointerEvent) => {
      if (!drag.at && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < TAP_SLOP) return;
      const r = this.svg.getBoundingClientRect();
      const inside = ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom;
      drag.at = inside ? this.clamp(type, this.toSvg(ev)) : { x: -999, y: -999 };
      this.render();
    };
    const up = () => {
      btn.removeEventListener("pointermove", move);
      btn.removeEventListener("pointerup", up);
      btn.removeEventListener("pointercancel", up);
      this.drag = null;
      if (!drag.at) this.addConnected(type); // nur getippt
      else if (drag.at.x > -999) this.addAt(type, drag.at);
      this.render();
      this.focusEdit();
    };
    btn.addEventListener("pointermove", move);
    btn.addEventListener("pointerup", up);
    btn.addEventListener("pointercancel", up);
  }

  // ---------------------------------------------------------------- Box-Inhalt bearbeiten

  private selectedBox(): FlowNode | undefined {
    const n = this.selection?.kind === "node" ? this.node(this.selection.id) : undefined;
    return n && isBox(n.type) ? n : undefined;
  }

  private focusEdit(): void {
    const input = this.editBar.querySelector<HTMLInputElement>("input");
    if (input && !input.value) input.focus();
  }

  private renderEditBar(): void {
    const n = this.selectedBox();
    this.editBar.hidden = !n;
    if (!n) { this.editBar.innerHTML = ""; return; }
    this.editBar.innerHTML = `
      <label class="edit-row"><span class="edit-type">${BOX_LABEL[n.type]}</span>
        <input type="text" value="${esc(n.text ?? "")}" placeholder="${esc(PLACEHOLDER[n.type])}"
          autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false"
          enterkeyhint="done" aria-label="Inhalt der Box"></label>
      <div class="keys">${SYMBOL_KEYS.map(k => `<button type="button" data-key="${esc(k)}">${esc(k)}</button>`).join("")}</div>
      <p class="edit-msg"></p>`;
    const input = this.editBar.querySelector("input")!;
    input.addEventListener("input", () => {
      const node = this.selectedBox();
      if (!node) return;
      if (!this.editing) { this.snapshot(); this.editing = true; }
      node.text = input.value;
      this.changed();
      this.render();
    });
    input.addEventListener("blur", () => { this.editing = false; });
    input.addEventListener("keydown", e => { if (e.key === "Enter") input.blur(); });
    this.editBar.querySelector(".keys")!.addEventListener("pointerdown", e => {
      const key = (e.target as HTMLElement).closest<HTMLElement>("[data-key]")?.dataset.key;
      if (!key) return;
      e.preventDefault(); // Fokus (und Tastatur) im Eingabefeld lassen
      const pad = /^[a-z]/.test(key) ? ` ${key} ` : key;
      const a = input.selectionStart ?? input.value.length, b = input.selectionEnd ?? a;
      input.setRangeText(pad, a, b, "end");
      input.dispatchEvent(new Event("input"));
      input.focus();
    });
  }

  private renderEditMsg(): void {
    const n = this.selectedBox();
    const msg = this.editBar.querySelector<HTMLElement>(".edit-msg");
    if (!n || !msg) return;
    let text = EXAMPLE[n.type], cls = "edit-msg";
    if ((n.text ?? "").trim()) {
      try {
        compileBox(n.type, n.text ?? "");
        text = "Inhalt ist gültig.";
        cls += " ok";
      } catch (err) {
        text = err instanceof ExprError ? err.message : String(err);
        cls += " bad";
      }
    }
    msg.className = cls;
    msg.textContent = text;
  }

  // ---------------------------------------------------------------- Darstellung

  private renderPalette(): void {
    this.palette.innerHTML = `<div class="palette-title">Bausteine</div>` +
      PALETTE.map(type => `<button class="part" data-type="${type}"
          title="${BOX_LABEL[type]} -- antippen (hängt unter die ausgewählte Box) oder auf die Fläche ziehen">
          ${boxSymbolSvg(type)}<span class="part-name">${BOX_LABEL[type]}</span></button>`).join("")
      + `<div class="trash-hint">Hierher ziehen zum Löschen</div>`;
    this.palette.querySelectorAll<HTMLElement>("button.part").forEach(btn =>
      btn.addEventListener("pointerdown", e => this.onPaletteDown(e, btn.dataset.type!, btn)));
  }

  private render(): void {
    const r = this.result;
    const run = this.runner;
    const bad = new Set<string>([
      ...(r?.problems ?? []).map(p => p.ref ?? ""),
      ...(run?.ref ? [run.ref] : []),
    ]);
    const parts: string[] = [];
    const edges = this.flow.edges.filter(e => this.node(e.to) && this.node(e.from.split(".")[0]));

    this.H = Math.max(MIN_H, ...this.flow.nodes.map(n => n.y! + size(n).h / 2 + 120));
    this.svg.setAttribute("viewBox", `0 0 ${W} ${this.H}`);

    // Eingänge, an denen noch kein Pfeil ankommt (sonst übernimmt die Pfeilspitze die Rolle)
    const dropTo = this.drag?.kind === "edge" && this.drag.moved ? this.nodeAt(this.drag.at)?.id : undefined;
    for (const n of this.flow.nodes) {
      if (n.type === "start" || (edges.some(e => e.to === n.id) && n.id !== dropTo)) continue;
      parts.push(`<polygon points="${tri(this.entryPos(n), false, 9)}" class="entry${n.id === dropTo ? " hot" : ""}"/>`);
    }

    // Pfeile; Rücksprünge bekommen je eine eigene Spur (kürzere innen)
    const back = edges.filter(e => this.entryPos(this.node(e.to)!).y - this.exitPos(e.from).y < 2 * STUB)
      .sort((a, b) => (this.exitPos(a.from).y - this.entryPos(this.node(a.to)!).y)
                    - (this.exitPos(b.from).y - this.entryPos(this.node(b.to)!).y));
    for (const e of edges) {
      const d = this.route(e.from, this.node(e.to)!, Math.max(0, back.indexOf(e)));
      const sel = this.selection?.kind === "edge" && this.selection.id === e.from;
      const hot = run?.lastExit === e.from;
      const k = sel ? "sel" : hot ? "hot" : "";
      parts.push(`<path d="${d}" class="edge-hit" data-edge="${esc(e.from)}"/>`,
                 `<path d="${d}" class="edge ${k}" marker-end="url(#arrow${k})"/>`);
    }

    // Knoten
    for (const n of this.flow.nodes) {
      const { w, h } = size(n);
      const sel = this.selection?.kind === "node" && this.selection.id === n.id ? " selected" : "";
      const cur = run && run.cur === n.id ? (run.status === "läuft" || run.status === "beendet" ? " current" : " failed") : "";
      const err = bad.has(n.id) ? " error" : "";
      const unreachable = r?.unerreichbar.includes(n.id) ? " unreachable" : "";
      let invalid = "", label: string;
      if (n.type === "start" || n.type === "ende") {
        label = `<text class="node-text term">${BOX_LABEL[n.type]}</text>`;
      } else {
        const text = (n.text ?? "").trim();
        if (text) {
          try { compileBox(n.type, text); } catch { invalid = " invalid"; }
          const maxW = n.type === "entscheidung" ? w * 0.62 : w - 44;
          const f = fit(text, maxW);
          label = `<text class="node-text" style="font-size:${f.size.toFixed(1)}px">${esc(f.s)}</text>`;
        } else {
          label = `<text class="node-text placeholder">${esc(PLACEHOLDER[n.type])}</text>`;
        }
      }
      const tag = n.type === "eingabe" || n.type === "ausgabe"
        ? `<text x="${-w / 2 + 22}" y="${-h / 2 - 8}" class="node-tag">${BOX_LABEL[n.type]}</text>` : "";
      parts.push(`<g class="node ${n.type}${sel}${cur}${err}${invalid}${unreachable}" data-node="${esc(n.id)}"
          transform="translate(${n.x} ${n.y})">
        <rect x="${-w / 2 - 6}" y="${-h / 2 - 6}" width="${w + 12}" height="${h + 12}" class="node-hit"/>
        ${shape(n.type)}${tag}${label}
      </g>`);
    }

    // Ausgänge (mit großem Fangbereich) und Ja/Nein-Beschriftung
    for (const n of this.flow.nodes) {
      for (const x of exits(n.type, n.id)) {
        const p = this.exitPos(x);
        const open = this.target(x) === undefined;
        const branch = x.split(".")[1];
        const err = bad.has(x) && open ? " error" : "";
        // Verbundene Ausgänge ohne Markierung (sonst Doppelpfeil-Optik); ziehen geht trotzdem
        const right = branch === "nein", s = 9;
        const tip = right ? { x: p.x + s, y: p.y } : { x: p.x, y: p.y + s };
        if (branch) {
          const lx = branch === "ja" ? p.x + 12 : p.x + 8, ly = branch === "ja" ? p.y + 26 : p.y - 12;
          parts.push(`<text x="${lx}" y="${ly}" class="exit-label">${branch}</text>`);
        }
        parts.push(`<g class="exit${err}" data-exit="${esc(x)}">
          <circle cx="${p.x}" cy="${p.y}" r="24" class="exit-hit"/>
          ${open ? `<polygon points="${tri(tip, right, s)}" class="exit-dot"/>` : ""}
        </g>`);
      }
    }

    // Laufende Interaktion
    const d = this.drag;
    if (d?.kind === "edge" && d.moved) {
      const s = this.exitPos(d.exit);
      const to = this.nodeAt(d.at);
      const t = to ? this.entryPos(to) : d.at;
      if (to) {
        const { w, h } = size(to);
        parts.push(`<rect x="${to.x! - w / 2 - 8}" y="${to.y! - h / 2 - 8}" width="${w + 16}" height="${h + 16}"
          rx="12" class="drop-target"/>`);
      }
      parts.push(`<line x1="${s.x}" y1="${s.y}" x2="${t.x}" y2="${t.y}" class="rubber" marker-end="url(#arrowsel)"/>`);
    }
    if (d?.kind === "new" && d.at && d.at.x > -999) {
      parts.push(`<g class="node ghost" transform="translate(${d.at.x} ${d.at.y})">${shape(d.type)}</g>`);
    }

    if (!this.flow.nodes.some(n => isBox(n.type))) {
      const start = this.node("s");
      const y = (start?.y ?? 70) + 150;
      parts.unshift(`<text x="${W / 2}" y="${y}" class="canvas-help">
        <tspan x="${W / 2}">Tippen Sie links auf einen Baustein:</tspan>
        <tspan x="${W / 2}" dy="30">Er wird unter die ausgewählte Box gehängt.</tspan>
        <tspan x="${W / 2}" dy="44">Pfeile ziehen Sie vom Dreieck unter einer Box</tspan>
        <tspan x="${W / 2}" dy="30">zur nächsten Box (z. B. für Schleifen).</tspan></text>`);
    }
    this.layer.innerHTML = parts.join("");
    this.root.querySelector<HTMLButtonElement>('[data-act="delete"]')!.disabled =
      !this.selection || (this.selection.kind === "node" && this.node(this.selection.id)?.type === "start");
    this.root.querySelector<HTMLButtonElement>('[data-act="undo"]')!.disabled = !this.undoStack.length;
    const hintBtn = this.root.querySelector<HTMLButtonElement>('[data-act="hint"]')!;
    const left = this.task.hinweise.length - this.hintsShown;
    hintBtn.innerHTML = `${ICON.bulb}<span>Hinweis${left > 0 ? ` (${left})` : ""}</span>`;
    hintBtn.disabled = left <= 0;
    hintBtn.hidden = this.task.hinweise.length === 0;
    this.renderEditMsg();
    this.renderSide();
  }

  private renderSide(): void {
    const t = this.task;
    const r = this.result;
    const run = this.runner;
    const list = (xs: number[]) => xs.join(", ");

    const rows = t.testfaelle.map((tf, i) => {
      const res = r?.testfaelle?.[i];
      const live = !res && run && i === this.caseIdx && run.status !== "läuft" ? run : null;
      const ist = res ? res.ausgabe : live ? live.ausgabe : null;
      let mark = "", cls = "";
      if (res) {
        cls = res.status === "richtig" ? "ok" : "bad";
        mark = res.status === "richtig" ? ICON.check
          : res.status === "falsch" ? ICON.cross : `<span class="why">${res.status === "endlosschleife" ? "∞" : "!"}</span>`;
      }
      return `<tr data-case="${i}" class="${i === this.caseIdx ? "current" : ""} ${cls}">
        <td class="num">${i + 1}</td>
        <td>${list(tf.eingabe)}</td>
        <td class="soll">${list(tf.ausgabe)}</td>
        <td class="ist">${ist === null ? "" : ist.length ? list(ist) : "–"}</td>
        <td class="mark">${mark}</td>
      </tr>`;
    }).join("");

    let feedback = "";
    if (r?.solved) {
      const stars = starsFor(r, this.hintsShown);
      const onward = this.next
        ? `<a class="next-task" href="#${this.next.id}">Nächste Aufgabe: ${esc(this.next.titel)} ${ICON.arrow}</a>`
        : `<a class="next-task" href="#">Zur Übersicht ${ICON.arrow}</a>`;
      feedback = `<div class="feedback success">
        <div class="stars${this.popStars ? " pop" : ""}">${"★".repeat(stars)}${"☆".repeat(3 - stars)}</div>
        <strong>Richtig!</strong> Alle ${t.testfaelle.length} Testfälle stimmen
        -- mit ${r.boxen} Box${r.boxen === 1 ? "" : "en"}.
        ${t.par !== null && !r.par_erreicht ? `<br>Es geht auch mit ${t.par} Boxen -- schaffen Sie das?` : ""}
        ${r.par_erreicht && this.hintsShown > 0 ? "<br>Den dritten Stern gibt es, wenn Sie es ohne Hinweis schaffen." : ""}
        ${onward}
      </div>`;
    } else if (r) {
      const msgs = r.problems.length
        ? r.problems.map(p => `<li>${esc(this.describe(p.ref))}${esc(p.message)}</li>`).join("")
        : r.testfaelle!.flatMap((c, i) => {
          if (c.status === "richtig") return [];
          const what = c.status === "falsch"
            ? `erwartet ${list(t.testfaelle[i].ausgabe) || "keine Ausgabe"}, ausgegeben ${list(c.ausgabe) || "nichts"}`
            : `${c.status === "endlosschleife" ? "Endlosschleife" : "Laufzeitfehler"}: ${c.fehler}`;
          return [`<li>Testfall ${i + 1}: ${esc(what)}</li>`];
        }).slice(0, 4).join("");
      const tip = r.problems.length ? "" : `<p>Verfolgen Sie den markierten Testfall mit „Schritt“.</p>`;
      feedback = `<div class="feedback error"><strong>Noch nicht ganz.</strong><ul>${msgs}</ul>${tip}</div>`;
    }

    const hints = t.hinweise.slice(0, this.hintsShown).map(h => `<li>${esc(h)}</li>`).join("");

    this.side.innerHTML = `
      ${feedback}
      <section class="cases">
        <h2>Testfälle</h2>
        <table class="case-table">
          <thead><tr><th></th><th>${esc(t.eingaben.join(", "))}</th><th class="soll">Soll</th>
            <th class="ist">Ist</th><th></th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </section>
      ${this.renderStepper()}
      ${hints ? `<div class="hints"><strong>Hinweise</strong><ul>${hints}</ul></div>` : ""}`;
  }

  /** „Ref“ eines Problems als lesbarer Vorspann, z. B. „Entscheidung b > a: “. */
  private describe(ref: string | null): string {
    if (!ref) return "";
    const [id, branch] = ref.split(".");
    const n = this.node(id);
    if (!n) return "";
    const text = (n.text ?? "").trim();
    return `${BOX_LABEL[n.type]}${text ? ` „${text}“` : ""}${branch ? ` (${branch})` : ""}: `;
  }

  private renderStepper(): string {
    const t = this.task;
    const tf = t.testfaelle[this.caseIdx];
    const prog = this.runner ? null : compileFlow(this.flow);
    const head = `<h2>Ablauf · Testfall ${this.caseIdx + 1}
      <small>${t.eingaben.map((name, k) => `${esc(name)} = ${tf.eingabe[k]}`).join(", ")}</small></h2>`;
    if (prog && prog.problems.length) {
      const items = prog.problems.slice(0, 4).map(p => `<li>${esc(this.describe(p.ref))}${esc(p.message)}</li>`).join("");
      return `<section class="stepper">${head}<p class="muted">Zum Ausprobieren fehlt noch:</p>
        <ul class="todo">${items}</ul></section>`;
    }
    const run = this.runner;
    const playing = this.playTimer !== null;
    const done = run !== null && run.status !== "läuft";
    const buttons = `<div class="run-buttons">
      <button type="button" data-run="restart" title="Von vorn" ${run ? "" : "disabled"}>${ICON.restart}</button>
      <button type="button" data-run="step" title="Ein Schritt" ${done ? "disabled" : ""}>${ICON.step}<span>Schritt</span></button>
      <button type="button" data-run="play" title="${playing ? "Anhalten" : "Abspielen"}" ${done && !playing ? "disabled" : ""}>
        ${playing ? ICON.pause : ICON.play}</button>
      <button type="button" data-run="end" title="Bis zum Ende" ${done ? "disabled" : ""}>${ICON.end}</button>
    </div>`;

    let status = `<p class="muted">Mit „Schritt“ läuft das Diagramm Box für Box. Die aktuelle Box leuchtet.</p>`;
    if (run) {
      const cur = this.node(run.cur);
      const what = cur && isBox(cur.type) ? `${BOX_LABEL[cur.type]} „${esc(cur.text ?? "")}“` : esc(BOX_LABEL[cur?.type ?? ""] ?? "");
      const expected = tf.ausgabe.join(", ");
      const list2 = (xs: number[]) => xs.length ? xs.join(", ") : "nichts";
      switch (run.status) {
        case "läuft":
          status = `<p>Schritt ${run.schritte} · als Nächstes: ${what}</p>`;
          break;
        case "beendet": {
          const ok = run.ausgabe.length === tf.ausgabe.length && run.ausgabe.every((v, i) => v === tf.ausgabe[i]);
          status = `<p class="${ok ? "ok" : "bad"}">${ok ? ICON.check : ICON.cross} Fertig nach ${run.schritte} Schritten:
            ausgegeben ${esc(list2(run.ausgabe))}${ok ? "" : `, erwartet ${esc(expected || "nichts")}`}.</p>`;
          break;
        }
        default:
          status = `<p class="bad">${run.status === "endlosschleife" ? "Endlosschleife" : "Laufzeitfehler"} in ${what}:
            ${esc(run.fehler ?? "")}</p>`;
      }
    }
    const vars = run && run.env.size
      ? `<table class="vars"><thead><tr><th>Variable</th><th>Wert</th></tr></thead><tbody>
          ${[...run.env].map(([k, v]) => `<tr class="${this.prevEnv.get(k) !== v ? "changed" : ""}">
            <td>${esc(k)}</td><td>${v}</td></tr>`).join("")}</tbody></table>`
      : "";
    const out = run && run.texte.length
      ? `<div class="console"><small>Ausgabe</small><pre>${esc(run.texte.join(" "))}</pre></div>` : "";
    return `<section class="stepper">${head}${buttons}${status}${vars}${out}</section>`;
  }
}
